import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { Batch } from '../types';
import { batchCache } from '../utils/cache';
import { useBrewfatherResource } from '../hooks/useBrewfatherResource';
import { useOptionalBrewery } from './BreweryContext';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import logger from '../utils/logger';

interface BatchContextType {
  // All batches data (complete and incomplete)
  allBatches: Batch[];
  // Filtered batches by status
  getBatchesByStatus: (statuses: string[]) => Batch[];
  // Filtered batches by completion status
  getBatchesByCompletion: (complete: boolean) => Batch[];
  // Loading states
  isLoading: boolean;
  isRefetching: boolean;
  // Error state
  error: string | null;
  // Actions
  refreshBatches: () => Promise<void>;
  refreshBatchesForStatuses: (statuses: string[]) => Promise<void>;
  refreshBatchesInBackground: () => Promise<void>;
  // Cache info
  lastUpdated: Date | null;
  cacheExpiry: Date | null;
  // Last sync info for header display
  lastSynced: Date | null;
  // Lazy load trigger
  ensureBatchesLoaded: () => Promise<void>;
  // Clear all in-memory data
  clearAllData: () => void;
}

const BatchContext = createContext<BatchContextType | undefined>(undefined);

interface BatchProviderProps {
  children: ReactNode;
  apiKey: string | null;
  breweryId?: string | null; // If set, read from Firestore instead of API
}

export const BatchProvider: React.FC<BatchProviderProps> = ({ children, apiKey, breweryId }) => {
  // Homebrewer path: Brewfather API + localStorage cache via the shared sync
  // layer. Disabled in brewery mode by passing a null apiKey.
  const resource = useBrewfatherResource<Batch>(
    {
      apiKey: breweryId ? null : apiKey,
      endpoint: 'batches',
      params: { complete: true },
      versionDomain: 'batches',
    },
    batchCache
  );

  // Brewery path: real-time listener on the brewery's server-synced batches.
  // Refresh actions trigger the brewerySync Cloud Function (admin only) and
  // onSnapshot delivers the results live.
  const breweryCtx = useOptionalBrewery();
  const [breweryBatches, setBreweryBatches] = useState<Batch[]>([]);
  const [breweryLoading, setBreweryLoading] = useState(false);
  const [breweryError, setBreweryError] = useState<string | null>(null);
  const [brewerySynced, setBrewerySynced] = useState<Date | null>(null);

  useEffect(() => {
    if (!breweryId) return;

    logger.log('BatchContext: Brewery mode - listening to Firestore for batches');
    setBreweryLoading(true);

    const batchesRef = collection(db, 'breweries', breweryId, 'batches');
    const unsubscribe = onSnapshot(
      batchesRef,
      (snapshot) => {
        const firestoreBatches: Batch[] = [];
        snapshot.forEach((doc) => {
          firestoreBatches.push(doc.data() as Batch);
        });
        firestoreBatches.sort((a, b) => (b._timestamp_ms || 0) - (a._timestamp_ms || 0));
        setBreweryBatches(firestoreBatches);
        setBrewerySynced(new Date());
        setBreweryLoading(false);
        setBreweryError(null);
      },
      (error) => {
        logger.error('BatchContext: Error listening to Firestore batches:', error);
        setBreweryError(error.message);
        setBreweryLoading(false);
      }
    );

    return () => unsubscribe();
  }, [breweryId]);

  const allBatches = breweryId ? breweryBatches : resource.items;

  const getBatchesByStatus = useCallback((statuses: string[]): Batch[] => {
    return allBatches.filter(batch => statuses.includes(batch.status));
  }, [allBatches]);

  const getBatchesByCompletion = useCallback((complete: boolean): Batch[] => {
    return allBatches.filter(batch => {
      const isComplete = batch.status === 'Completed' || batch.status === 'Conditioning';
      return complete ? isComplete : !isComplete;
    });
  }, [allBatches]);

  const noopAsync = useCallback(async () => {}, []);

  const clearAllData = useCallback(() => {
    setBreweryBatches([]);
    setBrewerySynced(null);
    setBreweryError(null);
    resource.clearAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource.clearAllData]);

  // Brewery mode: refresh triggers the server-side Brewfather sync. Members
  // can't call it (admin-only CF); their data updates live via onSnapshot.
  // Errors surface through syncError, so swallow the rejection here.
  const brewerySyncRefresh = useCallback(async () => {
    if (!breweryCtx?.canSync) return;
    await breweryCtx.syncBreweryData().catch(() => null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breweryCtx?.canSync, breweryCtx?.syncBreweryData]);

  const refreshBatches = breweryId ? brewerySyncRefresh : resource.refresh;

  // Status-specific refresh currently refreshes everything to stay consistent
  const refreshBatchesForStatuses = useCallback(async (_statuses: string[]) => {
    await refreshBatches();
  }, [refreshBatches]);

  const value: BatchContextType = useMemo(() => ({
    allBatches,
    getBatchesByStatus,
    getBatchesByCompletion,
    isLoading: breweryId ? breweryLoading : resource.isLoading,
    isRefetching: breweryId ? (breweryCtx?.isSyncing ?? false) : resource.isRefetching,
    error: breweryId ? (breweryError || breweryCtx?.syncError || null) : resource.error,
    refreshBatches,
    refreshBatchesForStatuses,
    refreshBatchesInBackground: breweryId ? brewerySyncRefresh : resource.refreshInBackground,
    lastUpdated: breweryId ? (breweryCtx?.lastSyncedAt ?? brewerySynced) : resource.lastUpdated,
    cacheExpiry: null,
    lastSynced: breweryId ? (breweryCtx?.lastSyncedAt ?? brewerySynced) : resource.lastSynced,
    ensureBatchesLoaded: breweryId ? noopAsync : resource.ensureLoaded,
    clearAllData,
  }), [
    allBatches, getBatchesByStatus, getBatchesByCompletion, refreshBatches,
    refreshBatchesForStatuses, clearAllData, noopAsync, brewerySyncRefresh,
    breweryId, breweryLoading, breweryError, brewerySynced,
    breweryCtx?.isSyncing, breweryCtx?.syncError, breweryCtx?.lastSyncedAt,
    resource.isLoading, resource.isRefetching, resource.error,
    resource.refreshInBackground, resource.lastUpdated, resource.lastSynced,
    resource.ensureLoaded,
  ]);

  return (
    <BatchContext.Provider value={value}>
      {children}
    </BatchContext.Provider>
  );
};

export const useBatches = (): BatchContextType => {
  const context = useContext(BatchContext);
  if (context === undefined) {
    throw new Error('useBatches must be used within a BatchProvider');
  }
  return context;
};
